import {Modal} from '@therms/atalaya'
import {
  ActivityTypeTagTitles,
  EventNames,
  LabeledInputDisplay,
  LocationNames,
  RecordTitles,
  ReportTypeNames,
  SEARCH_FILTER_FILTER_LABELS,
  SearchFilterActivityTypeMultiSelect,
  SearchFilterEventMultiSelect,
  SearchFilterLocationMultiSelect,
  SearchFilterRecordMultiSelect,
  SearchFilterReportTypeActiveInactiveMultiSelect,
  SearchFilterUserActiveInactiveMultiSelect,
  UserNames,
} from '@therms/react-modules'
import {useState} from 'react'

interface ReportsSearchFiltersModalProps {
  filtersState: {
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {
      start?: string | undefined
      end?: string | undefined
    }
    selectedReportTypeIds?: string[] | undefined
    selectedUserIds?: string[] | undefined
    selectedEventRefIds?: string[] | undefined
    selectedLocationRefIds?: string[] | undefined
    selectedRecordRefIds?: string[] | undefined
    selectedUserRefIds?: string[] | undefined
  }
  onClose: () => void
  setFiltersState: (
    val: Partial<{
      selectedActivityTypeIds: string[] | undefined
      selectedDateTimeRange: {
        start?: string | undefined
        end?: string | undefined
      }
      selectedReportTypeIds: string[] | undefined
      selectedUserIds: string[] | undefined
      selectedEventRefIds: string[] | undefined
      selectedLocationRefIds: string[] | undefined
      selectedRecordRefIds: string[] | undefined
      selectedUserRefIds: string[] | undefined
    }>,
  ) => void
  showLocationFilter?: boolean
}

function ReportsSearchFiltersModal({
  filtersState,
  onClose,
  setFiltersState,
  showLocationFilter = true,
}: ReportsSearchFiltersModalProps) {
  const [focusedFilter, setFocusedFilter] = useState<
    SEARCH_FILTER_FILTER_LABELS | ''
  >()

  return (
    <Modal
      closeHandler={onClose}
      escPressClose
      header="Advanced Search Filters"
      outsideClickClose
    >
      <div className="gap-y-xs flex flex-col">
        <div className="gap-xxs grid grid-cols-3 hover:cursor-pointer">
          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Activity Types"
              nameElement={
                <ActivityTypeTagTitles
                  activityTypeTagIds={
                    filtersState.selectedActivityTypeIds || []
                  }
                  listAll={false}
                />
              }
              value={filtersState.selectedActivityTypeIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.AUTHOR) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.AUTHOR)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Author"
              nameElement={
                <UserNames
                  userIds={filtersState.selectedUserIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedUserIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_REF) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.EVENT_REF)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Event Reference"
              nameElement={
                <EventNames
                  eventIds={filtersState.selectedEventRefIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedEventRefIds}
            />
          </div>

          {!!showLocationFilter && (
            <div
              onClick={() => {
                if (
                  focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION_REF
                ) {
                  setFocusedFilter('')
                } else {
                  setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.LOCATION_REF)
                }
              }}
            >
              <LabeledInputDisplay
                displayOnly
                label="Location Reference"
                nameElement={
                  <LocationNames
                    locationIds={
                      filtersState.selectedLocationRefIds
                        ? filtersState.selectedLocationRefIds
                        : []
                    }
                    listAll={false}
                  />
                }
                value={filtersState.selectedLocationRefIds}
              />
            </div>
          )}

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.RECORD_REF) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.RECORD_REF)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Record Reference"
              nameElement={
                <RecordTitles
                  recordIds={filtersState.selectedRecordRefIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedRecordRefIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.REPORT_TYPE) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.REPORT_TYPE)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Report Types"
              nameElement={
                <ReportTypeNames
                  reportTypeIds={filtersState.selectedReportTypeIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedReportTypeIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER_REF) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.USER_REF)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="User Reference"
              nameElement={
                <UserNames
                  userIds={filtersState.selectedUserRefIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedUserRefIds}
            />
          </div>
        </div>

        <SearchFilterActivityTypeMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE}
          onChange={(val) => {
            setFiltersState({selectedActivityTypeIds: val})
          }}
          onClearSelection={() => {
            setFiltersState({selectedActivityTypeIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE)
            }
          }}
          value={filtersState.selectedActivityTypeIds}
        />

        <SearchFilterUserActiveInactiveMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.AUTHOR}
          label="Author"
          onChange={(val) =>
            setFiltersState({
              selectedUserIds: val,
            })
          }
          onClearSelection={() => {
            setFiltersState({selectedUserIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.AUTHOR) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.AUTHOR)
            }
          }}
          value={filtersState.selectedUserIds}
        />

        <SearchFilterEventMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_REF}
          label="Event References"
          onChange={(val) => {
            setFiltersState({selectedEventRefIds: val})
          }}
          onClearSelection={() => {
            setFiltersState({selectedEventRefIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_REF) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.EVENT_REF)
            }
          }}
          value={filtersState.selectedEventRefIds}
        />

        {!!showLocationFilter && (
          <SearchFilterLocationMultiSelect
            focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION_REF}
            label="Location References"
            onChange={(val) => {
              setFiltersState({
                selectedLocationRefIds: val,
              })
            }}
            onClearSelection={() => {
              setFiltersState({selectedLocationRefIds: undefined})
            }}
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION_REF) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.LOCATION_REF)
              }
            }}
            value={
              filtersState.selectedLocationRefIds
                ? filtersState.selectedLocationRefIds
                : []
            }
          />
        )}

        <SearchFilterRecordMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.RECORD_REF}
          label="Record References"
          onChange={(val) =>
            setFiltersState({
              selectedRecordRefIds: val,
            })
          }
          onClearSelection={() => {
            setFiltersState({selectedRecordRefIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.RECORD_REF) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.RECORD_REF)
            }
          }}
          value={filtersState.selectedRecordRefIds}
        />

        <SearchFilterReportTypeActiveInactiveMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.REPORT_TYPE}
          onChange={(val) =>
            setFiltersState({
              selectedReportTypeIds: val,
            })
          }
          onClearSelection={() => {
            setFiltersState({selectedReportTypeIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.REPORT_TYPE) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.REPORT_TYPE)
            }
          }}
          value={filtersState.selectedReportTypeIds}
        />

        <SearchFilterUserActiveInactiveMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER_REF}
          label="User References"
          onChange={(val) =>
            setFiltersState({
              selectedUserRefIds: val,
            })
          }
          onClearSelection={() => {
            setFiltersState({selectedUserRefIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER_REF) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.USER_REF)
            }
          }}
          value={filtersState.selectedUserRefIds}
        />
      </div>
    </Modal>
  )
}
export {ReportsSearchFiltersModal}
